import {UploadType} from "./upload.types";
import {CmsApiService} from "../cms-api.service";

/**
 * Files above this size are not copied into memory; the multipart upload reads them part by part (P10-900).
 * Same value as the backend's MULTIPART_SETTINGS.threshold_bytes (server/multimedia/multipart_upload.py). The
 * backend still decides whether a file is sent in parts, so a mismatch only costs an in-memory copy, never a
 * wrong upload.
 */
export const LARGE_FILE_BYTES = 100 * 1024 * 1024;
/**
 * Largest file the backend accepts (MULTIPART_SETTINGS.max_file_bytes). Checked here first so the user gets
 * "file too large" at once; the backend still enforces it with a 400.
 */
export const MAX_UPLOAD_FILE_BYTES = 25 * 1024 * 1024 * 1024;
const EMPTY_FILE_MAX_ATTEMPTS = 5;
const EMPTY_FILE_RETRY_DELAY_MS = 250;
const JPEG_QUALITY = 0.92;

export function getUploadUrls(cms: CmsApiService): Record<UploadType, string> {
  return {
    [UploadType.IMAGE]: cms.getApiUrl(cms.API_IMAGE_ORDER_UPLOAD_URL, true),
    [UploadType.VIDEO]: cms.getApiUrl(cms.API_VIDEO_ORDER_UPLOAD_URL, true),
    [UploadType.ATTACHMENT]: cms.getApiUrl(cms.API_ATTACHMENT_ORDER_UPLOAD_URL, true),
    [UploadType.AUDIO]: cms.getApiUrl(cms.API_AUDIO_ORDER_UPLOAD_URL, true),
    [UploadType.MODEL3D]: cms.getApiUrl(cms.API_MODEL3D_ORDER_UPLOAD_URL, true),
  };
}

export function uploadTypeFromFile(file: File): UploadType {
  const fileType = file.type;
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
  if (fileType.startsWith('image/') || isLikelyHeic(file)) {
    return UploadType.IMAGE;
  } else if (fileType.startsWith('video/')) {
    return UploadType.VIDEO;
  } else if (fileType.startsWith('audio/')) {
    return UploadType.AUDIO;
  } else if (['model/gltf-binary', 'model/gltf+json'].includes(fileType) || ['glb', 'gltf'].includes(fileExtension)) {
    return UploadType.MODEL3D;
  } else {
    // Default to `attachment` for other file types
    return UploadType.ATTACHMENT;
  }
}

/**
 * Prepares a browser File for upload.
 *
 * Mobile Safari/Chrome often hand out lazy, revocable File references from the
 * camera or photo library. Those can report a non-zero `size` but later read as
 * empty once the tab has been backgrounded or an await (e.g. ordering an upload
 * URL) has run. Always copy bytes into an in-memory File before any other async
 * work. Also normalizes missing MIME / HEIC-HEIF for image uploads.
 */
export async function prepareFileForUpload(file: File, uploadType: UploadType): Promise<File> {
  if (!file) {
    throw new Error('EMPTY_FILE');
  }
  if (file.size > LARGE_FILE_BYTES) {
    return file;
  }

  let prepared = await materializeFile(file);

  if (uploadType === UploadType.IMAGE && needsImageNormalization(prepared)) {
    prepared = await convertImageToJpeg(prepared);
    console.warn('[P10-1503] converted to jpeg', {
      name: prepared.name,
      type: prepared.type,
      size: prepared.size,
    });
  }

  if (!prepared.size) {
    throw new Error('EMPTY_FILE');
  }

  return prepared;
}

export function contentTypeForUpload(file: File, uploadType: UploadType): string {
  if (file.type) {
    return file.type;
  }
  return uploadType === UploadType.IMAGE ? 'image/jpeg' : 'application/octet-stream';
}

export function isLikelyHeic(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return type.includes('heic') || type.includes('heif') ||
    name.endsWith('.heic') || name.endsWith('.heif');
}

/**
 * Snapshot a FileList/File[] synchronously on the change/drop event so later
 * async work cannot observe a revoked or emptied list.
 */
export function snapshotFiles(files: FileList | File[] | null | undefined): File[] {
  if (!files) {
    return [];
  }
  return Array.from(files);
}

function needsImageNormalization(file: File): boolean {
  // iOS camera captures often omit MIME type or deliver HEIC/HEIF.
  return !file.type || isLikelyHeic(file);
}

async function materializeFile(file: File): Promise<File> {
  let lastError: unknown;

  for (let attempt = 0; attempt < EMPTY_FILE_MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await delay(EMPTY_FILE_RETRY_DELAY_MS);
    }
    try {
      const buffer = await readFileAsArrayBuffer(file);
      if (buffer.byteLength > 0) {
        return new File([buffer], sanitizeFileName(file.name || 'image.jpg'), {
          type: file.type || '',
          lastModified: file.lastModified || Date.now(),
        });
      }
    } catch (e) {
      lastError = e;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('EMPTY_FILE');
}

/**
 * Read file bytes in a way that works in browsers and in Jest's File polyfill
 * (which may lack Blob.arrayBuffer).
 */
export async function readFileAsArrayBuffer(file: Blob): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }
  if (typeof Response !== 'undefined') {
    try {
      return await new Response(file).arrayBuffer();
    } catch {
      // Fall through to FileReader.
    }
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error || new Error('EMPTY_FILE'));
    reader.readAsArrayBuffer(file);
  });
}

function sanitizeFileName(name: string): string {
  // Safari can mishandle filenames containing narrow no-break spaces (U+202F)
  // and other non-breaking spaces from iOS/macOS screenshots.
  const cleaned = name.replace(/[\u202F\u00A0\u2007]/g, ' ').trim();
  return cleaned || 'image.jpg';
}

async function convertImageToJpeg(file: File): Promise<File> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('IMAGE_CONVERT_FAILED');
  }

  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    ctx.drawImage(bitmap, 0, 0);
  } catch {
    const objectUrl = URL.createObjectURL(file);
    try {
      const image = await loadHtmlImage(objectUrl);
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      ctx.drawImage(image, 0, 0);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } finally {
    bitmap?.close();
  }

  if (!canvas.width || !canvas.height) {
    throw new Error('IMAGE_CONVERT_FAILED');
  }

  const blob = await canvasToJpegBlob(canvas);
  const baseName = (file.name || 'camera').replace(/\.[^.]+$/, '') || 'camera';
  return new File([blob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

function canvasToJpegBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('IMAGE_CONVERT_FAILED')),
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}

function loadHtmlImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('IMAGE_CONVERT_FAILED'));
    image.src = src;
  });
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
